import { DATA_TYPE } from 'modules/FeeSchedules/FeeSchedule/Common.schema'
import moment, { Moment } from 'moment-timezone'
import { SelectOption } from 'types/entities'
import { ISelectIdOption } from 'types/forms'

export const FORMAT = 'MM-DD-YYYY'
export const DATE_PICKER_COMP_FORMAT = 'ddd MMM DD YYYY'
export const DATEPICKER_FORMAT = 'MM/dd/yyyy'
export const MDE_DATE_FORMAT = 'MM/DD/yyyy'
export const LISTING_FORMAT_DATEPICKER = 'MM-DD-YYYY, hh:mm A'
export const HOUR_FORMAT = 'MM-DD-YYYY hh:mm A'
export const DATEPICKER_FORMAT_WITH_TIME = 'MM/dd/yyyy h:mm aa'
export const FORMAT_WITH_TIME = 'MM-DD-YYYY hh:mm:ss A'
export const SLOT_TIME_FORMAT = 'hh:mm A'
export const SESSION_TIME_FORMAT = 'hh:mm:ss A'
export const HH_MM_SS_FORMAT = 'hh:mm:ss'
export const DATE_PICKER_FORMAT = 'MM-dd-yyyy'
export const CALENDAR_DATE_FORMAT = 'YYYY-MM-DD'
export const customTimeSlotGap = 15
export const getCurrentTimeZone = moment.tz.guess()
export const getCurrentDateNumber = 'D'
export const monthYearFormat = 'MMYYYY'
export const DATE_FORMAT_WITH_MMM = 'MMM,DD,YYYY [at] hh:mm a'
export const DATE_FORMAT_WITH_ddd = 'ddd, MMM D, YYYY'
export const DATE_FORMAT_WITH_MMMM = 'MMMM DD, YYYY'
export const COMMON_UTC_FORMAT = 'YYYY-MM-DDTHH:mm:ss.SSSZ'
export const COMMON_UTC_FORMAT_1 = 'YYYY-MM-DDTHH:mm:ss'
export const YearFormat = 'YY'
export const CURRENT_MONTH_FORMAT = 'MMMM'
export const START_OR_END_FORMAT = 'YYYY_MM_DDTHH_MM_SS'
export const MDE_FORMAT_1 = 'MMDDYYYY'
export const MDE_FORMAT_2 = 'MMYYYY'
export const DAY_FORMAT = 'DD MMM YYYY'
export const MONTH_YEAR_FORMAT = 'MM/YYYY'

// Define an enum for the units
export enum MOMENT_UNIT {
	YEAR = 'year',
	MONTH = 'month',
	DAY = 'day',
	WEEK = 'week',
	QUARTER = 'quarter',
}

type MomentUnits = 'year' | 'month' | 'day' | 'week' | 'quarter'

// Return the type of format of date send for conversion in all moment functions
export const dateFormatType = (date: Date | null | string | number = '') => {
	const value = date
	if (typeof value !== DATA_TYPE.OBJECT) {
		const format1 = isValidDate(value, FORMAT_WITH_TIME) // strict parsing
		const format2 = isValidDate(value, FORMAT)
		const format3 = isValidDate(value, SLOT_TIME_FORMAT)
		const format4 = isValidDate(value, HOUR_FORMAT)
		const format5 = isValidDate(value, YearFormat)
		const format6 = isValidDate(value, COMMON_UTC_FORMAT)
		const format7 = isValidDate(value, HH_MM_SS_FORMAT)
		const format8 = isValidDate(value, DATE_FORMAT_WITH_ddd)
		const format9 = isValidDate(value, START_OR_END_FORMAT)
		const format10 = isValidDate(value, MDE_FORMAT_1)
		const format11 = isValidDate(value, MDE_FORMAT_2)
		const format12 = isValidDate(value, DAY_FORMAT)
		const format13 = isValidDate(value, MONTH_YEAR_FORMAT)
		if (format1) {
			return FORMAT_WITH_TIME
		} else if (format2) {
			return FORMAT
		} else if (format3) {
			return SLOT_TIME_FORMAT
		} else if (format4) {
			return HOUR_FORMAT
		} else if (format5) {
			return YearFormat
		} else if (format6) {
			return COMMON_UTC_FORMAT
		} else if (format7) {
			return HH_MM_SS_FORMAT
		} else if (format8) {
			return DATE_FORMAT_WITH_ddd
		} else if (format9) {
			return START_OR_END_FORMAT
		} else if (format10) {
			return MDE_FORMAT_1
		} else if (format11) {
			return MDE_FORMAT_2
		} else if (format12) {
			return DAY_FORMAT
		} else if (format13) {
			return MONTH_YEAR_FORMAT
		} else {
			return ''
		}
	}
}

// Convert the date into timestamp
export const convertUnix = (date: Date | null | string = toDate()) => {
	if (dateFormatType(date)) {
		return moment.utc(date, dateFormatType(date)).unix() * 1000
	} else {
		return moment.utc(date).unix() * 1000
	}
}

// Convert the date into timestamp
export const convertUnixDate = (date: Date | null | string = toDate()) => {
	if (dateFormatType(date)) {
		return moment(date, dateFormatType(date)).unix() * 1000
	} else {
		return moment(date).unix() * 1000
	}
}

// Convert the date into timestamp with the time 11:59:59 pm
export const convertUnixWithEndOf = (
	date: Date | null | string = toDate(),
	durationType: any = 'day',
) => {
	const endOf = moment.utc(date, dateFormatType(date)).endOf(durationType)
	const addDay = endOf.add(1, durationType)
	return addDay.unix() * 1000
}

// Convert the date into timestamp with the time 00:00:00 am
export const convertUnixWithStartOf = (
	date: Date | null | string = toDate(),
	format = FORMAT,
	durationType: MomentUnits = MOMENT_UNIT.DAY,
) => {
	const startOf = startOfDay(date, durationType, format)
	return moment.utc(startOf).unix() * 1000
}

// Function to safely parse a date input into a moment object
export const parseDateMoment = (date: Date | null | string | number = '') => {
	const formattedDate = moment(date, dateFormatType(date), true)
	return formattedDate.isValid() ? formattedDate : moment.invalid()
}

// Convert unix into date or change the date into different type of formats.
export const formatDate = (
	date: Date | null | string | undefined | number = toDate(),
	format = FORMAT,
) => {
	return moment(date, dateFormatType(date)).format(format).toString().trim()
}

// Convert unix into date or change the date into different type of formats.
export const formatDateFromCurrent = (
	date: Date | null | string | undefined | number = toDate(),
	currentFormat = FORMAT,
	format = FORMAT,
) => {
	return moment(date, currentFormat).format(format).toString().trim()
}

// Convert unix into date or change the date into different type of formats in UTC format.
export const formatDateUTC = (
	date: Date | null | string | undefined | number = new Date(),
	format = FORMAT,
) => {
	return moment.utc(date, dateFormatType(date)).format(format).toString().trim()
}

// get date differance in days
export const getDateDifferenceInDays = (startDate: any, endDate: any): number => {
	const start = toDate(startDate, dateFormatType(startDate)).getTime()
	const end = toDate(endDate, dateFormatType(endDate)).getTime()
	return (end - start) / (1000 * 60 * 60 * 24) // Convert milliseconds to days
}

// timestamp into date
export const formatTimestamp = (date: Date | string | number = toDate(), format = FORMAT) => {
	return moment(date, dateFormatType(date)).format(format).toString().trim()
}

// Break date into slices for the MDE part.
export function convertToMMDDYY(dateString: string) {
	const isDate = dateString.slice(0, 2)
	const isMonth = dateString.slice(3, 5)
	const isCentury = +dateString.slice(6, 5) - 1
	const isYear = dateString.slice(6, 8)
	return `${isMonth}${isDate}${isCentury}${isYear}`
}

// Convert the date into the Date type object mainly used for the date-pickers.
export const toDate = (date?: Date | null | string | undefined | number, format = FORMAT) => {
	return moment(date, dateFormatType(date)).utc().toDate()
}

export const toDateFromNextDay = (date?: Date | null | string | undefined | number) => {
	return moment(date, dateFormatType(date)).add(1, 'days').toDate()
}
export const checkIsEndDateSameOrSmall = ({
	startDate,
	endDate,
}: {
	startDate: Date | null | string | undefined | number
	endDate: Date | null | string | undefined | number
}) => {
	const isEndDateSameOrSmall =
		moment(endDate, dateFormatType(endDate)).diff(moment(startDate, dateFormatType(startDate))) <
			0 ||
		moment(endDate, dateFormatType(endDate)).isSame(
			moment(startDate, dateFormatType(startDate)),
			MOMENT_UNIT.DAY,
		)
	return isEndDateSameOrSmall
}

export const checkIsDateSame = ({
	startDate,
	endDate,
}: {
	startDate: Date | null | string | undefined | number
	endDate: Date | null | string | undefined | number
	unit?: string
}) => {
	return moment(endDate, dateFormatType(endDate)).isSame(
		moment(startDate, dateFormatType(startDate)),
		MOMENT_UNIT.DAY,
	)
}

function getMonthDifference(startDate: Moment, endDate: Moment): number {
	let monthsDiff = endDate.diff(startDate, MOMENT_UNIT.MONTH)
	const startPlusMonths = startDate.clone().add(monthsDiff, MOMENT_UNIT.MONTH)
	if (endDate.isAfter(startPlusMonths, 'day')) {
		monthsDiff++
	}

	return monthsDiff
}

export const checkTimeDifference = (
	startDate: string | Date | null,
	endDate: string | Date | null,
	format: string = FORMAT,
	timeFormat?: any,
) => {
	const start = moment(startDate, format)
	const end = moment(endDate, format)
	if (timeFormat === MOMENT_UNIT.MONTH) {
		return getMonthDifference(start, end)
	} else {
		return moment(end, format).diff(moment(start, format), timeFormat)
	}
}

export const getAllCountryTimezones = () => {
	const currentCode = 'US'
	const usTimezones = moment.tz.zonesForCountry(currentCode) || []
	const extraTimezones = ['Pacific/Saipan', 'Pacific/Pago_Pago', 'Pacific/Guam']
	const allTimezones = Array.from(new Set([...usTimezones, ...extraTimezones]))
	return allTimezones
}

export const checkSlotInRange = (
	startTime: string,
	endTime: string,
	currentTime: string,
	format: string = SLOT_TIME_FORMAT,
) => {
	const start = moment(startTime, format)
	const end = moment(endTime, format)
	const current = moment(currentTime, format)
	return moment(current, format).isBetween(moment(start, format), moment(end, format))
}

export const mergeCurrentTimeWithTs = (
	dateTime: Date | null | string | undefined | number = toDate(),
) => {
	const time = moment().format(SLOT_TIME_FORMAT)
	const date = formatDate(dateTime)
	const newDate = formatDate(date + ' ' + time, FORMAT_WITH_TIME)
	return convertUnix(newDate)
}

export const monthConvert = (
	month: null | string | number | any,
	date: Date | null | string = toDate(),
) => {
	return moment.utc(date, FORMAT_WITH_TIME).add(month, 'months').unix() * 1000
}

export const addDateConverter = (
	date: Date | null | string | undefined = toDate(),
	count: number | null = 0,
	format: string = FORMAT,
	type: any = 'days',
) => {
	return moment(date, dateFormatType(date)).add(count, type).format(format)
}

export const getEndOf = (
	date?: Date | null | string | undefined,
	format: string = FORMAT,
	name: any = MOMENT_UNIT.DAY,
) => {
	return moment(date, dateFormatType(date)).endOf(name).format(format)
}

export const timeFormat = (time: string | null) => {
	return moment(time, 'hh:mm A').unix() * 1000
}

export const filterDateTime = (timeStampDate: any) => {
	const date = toDate(timeStampDate)

	// Extracting individual date and time components
	const hours = date.getHours()
	const minutes = date.getMinutes()
	const seconds = date.getSeconds()
	const formattedTime = `${hours}:${minutes}:${seconds}`

	return formattedTime
}

export const FilterTime = (time: string | null | number) => {
	return moment(time, dateFormatType(time)).format('h:mm:ss A')
}

export const convertUnixWithTime = (date: Date | null | string = toDate()) => {
	return moment.utc(date, FORMAT_WITH_TIME).unix() * 1000
}

export const calculateYear = (givenDate: string | null = null) => {
	const birthDate = moment(givenDate, 'YYYY-DD-MM')
	const currentDate = moment()
	return currentDate.diff(birthDate, 'years')
}

export function getAge(dateString: string) {
	return moment().diff(moment(dateString, dateFormatType(dateString)), 'years')
}
export const convertUTCUnix = (date: Date | null | string = toDate()) => {
	const localDate = moment(date, 'MM-DD-YYYY h:mma')

	// Convert the parsed date to UTC
	const utcDate = localDate.utc()

	// Get the UTC timestamp
	return utcDate.valueOf()
}

export const validateFutureDate = ({
	selectedDate = null,
}: {
	selectedDate: Date | null | undefined
}) => {
	// Get the start of today and end of today
	const startOfToday = moment().startOf(MOMENT_UNIT.DAY).toDate()
	const endOfToday = moment().endOf(MOMENT_UNIT.DAY).toDate()
	// Determine if the selected date is today
	const isToday =
		selectedDate &&
		moment(selectedDate, dateFormatType(selectedDate)).isSame(moment(), MOMENT_UNIT.DAY)
	// Define the minTime and maxTime conditionally based on whether the selected date is today
	const minTime = isToday ? startOfToday : moment('1920-01-01 00:00', 'YYYY-MM-DD HH:mm').toDate()
	const maxTime = isToday ? toDate() : endOfToday
	return {
		minTime,
		maxTime,
	}
}

export const disabledPastDate = ({
	selectedDate = null,
}: {
	selectedDate: Date | null | undefined
}) => {
	// Get the start of today and end of today
	const startOfToday = moment().startOf(MOMENT_UNIT.DAY).toDate()
	const endOfToday = moment().endOf(MOMENT_UNIT.DAY).toDate()
	// Determine if the selected date is today
	const isToday =
		selectedDate &&
		moment(selectedDate, dateFormatType(selectedDate)).isSame(moment(), MOMENT_UNIT.DAY)
	const minTime = isToday ? toDate() : startOfToday
	const maxTime = endOfToday
	return {
		minTime,
		maxTime,
	}
}

export const formatNumber = ({
	value = 0,
	toFixed = null,
}: {
	value: number
	toFixed: number | null
}) => {
	// Convert the number to a fixed-point notation string with two decimal places
	let numStr = value.toString()
	const isDecimal = value % 1 != 0
	if (isDecimal && toFixed) {
		numStr = Number(value).toFixed(toFixed)
	}

	// Split the number into integer and decimal parts.
	const [integerPart, decimalPart] = numStr.split('.')

	// Add commas to the integer part in the Indian numbering format
	let lastThree = integerPart.slice(-3)
	const otherNumbers = integerPart.slice(0, -3)

	if (otherNumbers !== '') {
		lastThree = ',' + lastThree
	}
	const formattedInteger = otherNumbers.replace(/\B(?=(\d{2})+(?!\d))/g, ',') + lastThree

	// Join the integer and decimal parts
	const formattedNumber = isDecimal ? `${formattedInteger}.${decimalPart}` : `${formattedInteger}`

	return formattedNumber
}

export function timezoneDropdownOptions() {
	return (
		getAllCountryTimezones()?.map((item) => {
			const tzName = moment.tz(item).format('z')
			return new SelectOption(item, `${item} - ${tzName}`)
		}) || []
	)
}

export const startOfDay = (
	date: Date | null | string | undefined | number = toDate(),
	unit: MomentUnits = MOMENT_UNIT.DAY,
	format = FORMAT,
) => {
	return moment(date, dateFormatType(date)).startOf(unit).format(format).toString().trim()
}

export const endOfDay = (
	date: Date | null | string | undefined | number = toDate(),
	unit: MomentUnits = MOMENT_UNIT.DAY,
	format = FORMAT,
) => {
	return moment(date, dateFormatType(date)).endOf(unit).format(format).toString().trim()
}

export const fetchDate = (date?: string) => {
	return moment(date, dateFormatType(date))
}
export const isValidDate = (date: Date | null | string | number, formatType: string) => {
	return moment(date, formatType, true).isValid()
}

export const checkValidDate = (date: Date | null | string | number) => {
	return moment(date, true).isValid()
}

// Add date .
export const addDate = (
	date: Date | null | string | undefined | number = toDate(),
	duration = 0,
	durationType: MomentUnits = MOMENT_UNIT.DAY,
) => {
	return moment(date, dateFormatType(date)).add(duration, durationType).toString().trim()
}

// Subtract date .
export const subtractDate = (
	date: Date | null | string | undefined | number = toDate(),
	format = FORMAT,
	duration = 0,
	durationType: MomentUnits = MOMENT_UNIT.DAY,
) => {
	return moment(date, dateFormatType(date))
		.subtract(duration, durationType)
		.format(format)
		.toString()
		.trim()
}

export function isStartDateValid(startDate: number) {
	const today = moment.utc().startOf(MOMENT_UNIT.DAY) // today's date in UTC (00:00:00)
	const start = moment.utc(startDate).startOf(MOMENT_UNIT.DAY) // startDate in UTC
	return start.isBefore(today) || start.isSame(today)
}

export function isMeetingInFuture(startDate: number) {
	const start = moment()
	const meetingTime = moment(startDate)
	return meetingTime.isAfter(start)
}

export function isMeetingAfterEndTime(endDate: number) {
	const end = moment()
	const meetingTime = moment(endDate)
	return end.isAfter(meetingTime) || end.isSame(meetingTime)
}

export function getMonthName(date: string, dateFormat = 'YYYY-MM') {
	return moment.utc(date, dateFormat).format('MMM YY')
}

export function getDayWithMonth(date: string, dateFormat = CALENDAR_DATE_FORMAT) {
	return moment.utc(date, dateFormat).format('D MMM')
}

export function getDayName(fullDay: string) {
	return moment(fullDay, 'dddd').format('ddd').toLowerCase()
}

export const isCompareDateSame = (
	compareDateFrom: Date | string,
	compareDateTo: Date | string,
	comparisonType = MOMENT_UNIT.DAY,
) => {
	return moment(compareDateFrom, dateFormatType(compareDateFrom)).isSame(
		compareDateTo,
		comparisonType,
	)
}

export const getTimeDifference = (
	compareDateFrom: Date | Moment | string,
	compareDateTo: Date | Moment | null,
) => {
	const time1 = moment(compareDateFrom)
	const time2 = moment(compareDateTo)
	return time1.diff(time2, 'minutes')
}

// Convert the date into timestamp with the time 11:59:59 pm
export const convertUnixWithEndDate = (
	date: Date | null | string = toDate(),
	durationType: any = 'day',
) => {
	const endOf = moment.utc(date, dateFormatType(date)).endOf(durationType)
	return endOf.unix() * 1000
}

export const combineDateAndTime = ({ time, date }: { time: string; date: string }) => {
	return formatDate(date + ' ' + time, FORMAT_WITH_TIME)
}

export function getPreferredSlot(
	slots: ISelectIdOption<number, string, string>[],
): ISelectIdOption<number, string, string> | null {
	if (!slots || slots.length === 0) return null
	const now = moment()
	const today = now.format(FORMAT)
	const todaySlot = slots.find((slot, i) => {
		return moment(slot.value).format(FORMAT) === today
	})
	if (todaySlot) return todaySlot
	const pastSlots = slots
		.filter((slot) => moment(slot.value).isBefore(now))
		.sort((a, b) => b.value - a.value)
	if (pastSlots.length > 0) {
		return pastSlots[0]
	}
	return slots?.[0]
}

export function addMinutesToExisting(currentTime: Date, minutes: number): Date {
	return moment(currentTime).add(minutes, 'minutes').toDate()
}

export function subtractMinutes(duration: number, timestamp: number): number {
	return moment(timestamp).subtract(duration, 'minutes').valueOf()
}
